import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";

import { toast } from "react-toastify";
import { getSocket } from "../../services/socket-io";
import clsx from "clsx";

import { Paper, makeStyles, Button, Tooltip, IconButton, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useThemeContext } from "../../context/DarkMode";
import GetAppIcon from "@material-ui/icons/GetApp";
import { AuthContext } from "../../context/Auth/AuthContext";

import ContactDrawer from "../ContactDrawer";
import AppointmentDrawer from "../AppointmentDrawer";
import MessageInput from "../MessageInput/";
import SignatureOnboardingBanner from "../SignatureOnboardingBanner";
import UserModal from "../UserModal";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import ChatConnectionBar from "../ChatConnectionBar";
import QuickNotesPopover from "../QuickNotesPopover";
import SupportButton from "../SupportButton";
import ModuleErrorBoundary from "../ModuleErrorBoundary";
import TicketPendingButton from "../TicketPendingButton";
import ContactStateChip from "../ContactStateChip";
import AppointmentButton from "../AppointmentButton";
import { resolvePending } from "../../services/ticketPendingService";
import MessagesList from "../MessagesList";
import useResyncOnResume from "../../hooks/useResyncOnResume";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { useChatHighlight } from "../../context/ChatHighlight/ChatHighlightContext";
import { pooledModeFromSetting } from "../../helpers/canOpenTicket";
import { resolveTakeoverQueue } from "../../helpers/takeoverQueue";
import SelectQueueOnAcceptModal from "../SelectQueueOnAcceptModal";

// ContactDrawer is persistent (320px) and AppointmentDrawer is persistent (380px).
// Both use position:absolute within #drawer-container.
// mainWrapper compensates with negative marginRight based on open state.
const contactDrawerWidth = 320;
const appointmentDrawerWidth = 380;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  // FIX 1: ticketInfo shrinks (not the action buttons), minWidth:0 prevents overflow
  ticketInfo: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "80%",
      flexBasis: "80%",
    },
  },
  // FIX 1: action buttons never shrink so RESOLVER / 3-dot menu stay visible
  ticketActionButtons: {
    flexShrink: 0,
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: "5px",
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    // FIX 2: marginRight is set via inline style (computed from both drawers)
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();
  const theme = useTheme();
  // feat_142: <1280px → header del chat compacto (chip más chico, gap reducido,
  // botones DEVOLVER/RESOLVER como íconos con tooltip).
  // feat_328: la densidad "compact" activa el modo compacto también en
  // pantallas grandes (el viewport solo ya no alcanza como criterio).
  const { density } = useThemeContext();
  const isCompact =
    useMediaQuery(theme.breakpoints.down("md")) || density === "compact";
  const { user } = useContext(AuthContext);

  // Fix 5: modo vista previa — no se marcan mensajes como leídos
  const readOnly = location.state?.readOnly === true;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [appointmentDrawerOpen, setAppointmentDrawerOpen] = useState(false);
  const [signatureFeatureEnabled, setSignatureFeatureEnabled] = useState(false);
  const [userProfileModalOpen, setUserProfileModalOpen] = useState(false);
  // feat_295: modo "EN ATENCIÓN POR <asesor>" (off | visual | lock).
  const [attendingMode, setAttendingMode] = useState("off");
  // feat_338: pool de resueltos (off | queue | company). Solo lo usa el ATENDER
  // de readOnly, para mover el ticket a mi cola al tomar un cerrado ajeno.
  const [pooledMode, setPooledMode] = useState("off");
  // feat_345: con varias colas propias, el ATENDER de vista previa pregunta a
  // cuál va el cerrado ajeno en vez de clavar la primera.
  const [selectQueueModalOpen, setSelectQueueModalOpen] = useState(false);

  // Close appointment drawer when navigating to a different ticket
  useEffect(() => {
    setAppointmentDrawerOpen(false);
  }, [ticketId]);

  // ChatHighlight: al entrar al chat, limpiar el resaltado "requiere atención".
  const { clearHighlight } = useChatHighlight();
  useEffect(() => {
    if (ticketId) clearHighlight(ticketId);
  }, [ticketId, clearHighlight]);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      const s = Array.isArray(data) && data.find(x => x.key === "signatureEnabled");
      setSignatureFeatureEnabled(s?.value === "enabled");
      // feat_295: attendingMode (default "off"). Reusa este mismo fetch, no re-pide.
      const am = Array.isArray(data) && data.find(x => x.key === "attendingMode");
      setAttendingMode(am?.value || "off");
      // feat_338: mismo fetch, alias legacy mapeado por el helper compartido.
      const pm = Array.isArray(data) && data.find(x => x.key === "closedTicketsPooledByQueue");
      setPooledMode(pooledModeFromSetting(pm?.value));
    }).catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [incomingPendingAlert, setIncomingPendingAlert] = useState(null);
  // feat_172/173 — typing/recording presence indicator (whaileys only)
  const [presence, setPresence] = useState(null);
  const presenceTimerRef = useRef(null);

  // feat_346 — re-sync al despertar: re-pide GET /tickets/:id (idempotente y
  // barato) para que la cabecera/estado del chat no queden viejos tras una
  // suspensión. Sin RESET porque acá no hay lista que fusionar.
  const [resyncKey, setResyncKey] = useState(0);
  useResyncOnResume(() => setResyncKey(k => k + 1));

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);

          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          const status = err?.response?.status;
          if (status === 403 || status === 404) {
            // todo_044 — el restore de pestaña (TicketsManager) guarda el último
            // ticket visto por tab en `lastTicket_<tab>`. Si ese ticket dejó de
            // ser accesible (transferencia, cambio de colas), el restore lo
            // reintenta en cada cambio de pestaña y rebota una y otra vez.
            // Se limpia la key acá, en el único punto donde consta que no lo es.
            try {
              Object.keys(localStorage)
                .filter(k => k.startsWith("lastTicket_"))
                .forEach(k => {
                  if (localStorage.getItem(k) === String(ticketId)) {
                    localStorage.removeItem(k);
                  }
                });
            } catch (_) {}
            // feat_337 — el texto estaba HARDCODEADO en español (regresión de la
            // regla de i18n de feat_335). El gate real sigue siendo el backend:
            // este toast es la última red, no la primera (ver helpers/canOpenTicket).
            toastError({
              response: { data: { message: i18n.t("tickets.noAccess") } }
            });
            history.push("/tickets");
          } else {
            toastError(err);
          }
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, history, resyncKey]);

  useEffect(() => {
    // SINGLETON getSocket() + join inmediato + re-join en connect + cleanup con off.
    // NUNCA openSocket()+disconnect(): en socket.io-client v3 el socket es multiplexado
    // (compartido con MessagesList/MessageInput/ChatConnectionBar); un disconnect() lo
    // tira abajo para todos y no auto-reconecta.
    const socket = getSocket();

    // En readOnly no emitimos joinChatBox para no marcar mensajes como leídos
    if (!readOnly) socket.emit("joinChatBox", ticketId);
    const handleReconnect = () => {
      if (!readOnly) socket.emit("joinChatBox", ticketId);
    };
    socket.on("connect", handleReconnect);

    // feat_280 — Gate por ticketId en AMBOS branches. El socket es SINGLETON
    // multiplexado (compartido con NotificationsPopOver que une notification-${companyId}),
    // así que recibe eventos "ticket" de TODA la empresa. Sin gate, un update de OTRO
    // ticket pisaba el ticket abierto (setTicket con el ajeno) → ChatConnectionBar mostraba
    // la conexión equivocada y los botones de acción operaban sobre el ticket equivocado;
    // un delete de OTRO ticket sacaba al usuario del chat abierto. Espejo de handleAppMessage.
    const handleTicket = (data) => {
      if (
        data.action === "update" &&
        String(data.ticket?.id) === String(ticketId)
      ) {
        setTicket(data.ticket);
      }

      // `reason: "status_change"` = el ticket NO se borró, solo cambió de status
      // (o de dueño) y el backend avisa a la sala del status viejo para que esa
      // lista lo suelte. Este socket es SINGLETON multiplexado y está unido a las
      // 3 salas de status a la vez, así que ese delete también llega acá: sin el
      // gate, cerrar/transferir/auto-cerrar un ticket desde otra pestaña expulsaba
      // al asesor del chat abierto con el toast "Ticket deleted sucessfully".
      // El update que viaja junto (sala `${ticketId}`) refresca el ticket en pantalla.
      if (
        data.action === "delete" &&
        data.reason !== "status_change" &&
        String(data.ticketId) === String(ticketId)
      ) {
        toast.success("Ticket deleted sucessfully.");
        history.push("/tickets");
      }
    };
    socket.on("ticket", handleTicket);

    // feat_200 (Opción B) — refrescar el prop ticket con los campos de la ventana 24hs
    // al llegar un inbound del cliente. El backend actualiza lastCustomerMessageAt ANTES
    // de emitir el appMessage, así que data.ticket ya viene fresco. Doble garantía junto
    // al localInboundAt de ChatConnectionBar/MessageInput: el header recalcula la ventana
    // desde el prop aunque el override local fallara.
    const handleAppMessage = (data) => {
      if (
        data.action === "create" &&
        String(data.ticket?.id) === String(ticketId) &&
        data.message?.fromMe === false
      ) {
        setTicket((prev) => ({
          ...prev,
          lastCustomerMessageAt: data.ticket.lastCustomerMessageAt,
          lastCustomerMessageWhatsappId: data.ticket.whatsappId,
        }));
      }
    };
    socket.on("appMessage", handleAppMessage);

    const handleContact = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };
    socket.on("contact", handleContact);

    const handleTicketPending = (data) => {
      if (
        data.action === "incoming_message" &&
        String(data.ticketId) === String(ticketId)
      ) {
        setIncomingPendingAlert(data.pending);
      }
    };
    socket.on("ticketPending", handleTicketPending);

    // feat_172/173 — presencia (Escribiendo/Grabando). Solo whaileys emite.
    const handleContactPresence = (data) => {
      if (!data || String(data.ticketId) !== String(ticketId)) return;
      if (data.state === "composing" || data.state === "recording") {
        setPresence({
          state: data.state,
          participantName: data.participantName,
          isGroup: data.isGroup,
        });
        clearTimeout(presenceTimerRef.current);
        // auto-reset si no llega "available" (ts del backend)
        presenceTimerRef.current = setTimeout(() => setPresence(null), 8000);
      } else {
        clearTimeout(presenceTimerRef.current);
        setPresence(null);
      }
    };
    socket.on("contactPresence", handleContactPresence);

    return () => {
      clearTimeout(presenceTimerRef.current);
      setPresence(null);
      socket.off("connect", handleReconnect);
      socket.off("ticket", handleTicket);
      socket.off("appMessage", handleAppMessage);
      socket.off("contact", handleContact);
      socket.off("ticketPending", handleTicketPending);
      socket.off("contactPresence", handleContactPresence);
    };
  }, [ticketId, history, readOnly]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleExportTicket = async () => {
    try {
      const response = await api.get(`/tickets/${ticketId}/export.txt`, {
        responseType: "blob"
      });
      const blob = new Blob([response.data], { type: "text/plain;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers["content-disposition"];
      let filename = `waia-ticket-${ticketId}.txt`;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // feat_348 — el request usa responseType:"blob", así que ante un error el
      // cuerpo llega como Blob y toastError no encuentra data.message ni
      // data.error → toast genérico "Error interno del servidor". Eso mantuvo
      // muda por 2,5 meses la regresión de feat_185. Leemos el Blob y
      // re-inyectamos el JSON antes de toastear. Defensivo: si algo falla acá,
      // cae al comportamiento anterior (toast genérico) → cero regresión.
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          try {
            err.response.data = JSON.parse(text);
          } catch {
            err.response.data = { message: text };
          }
        } catch {
          // Blob ilegible: dejamos el error como vino.
        }
      }
      toastError(err);
    }
  };

  // feat_345 — ATENDER de la vista previa. Antes reimplementaba INLINE la regla
  // de cola de feat_338 (una copia a mano de la de TicketActionButtons); ahora
  // las dos consumen `helpers/takeoverQueue`. Mismo patrón de re-entrada que
  // TicketListItem.handleAcepptTicket: con varias colas el handler sale ANTES
  // del PUT, el modal elige, y onConfirm lo vuelve a llamar con el override.
  const handleTakeoverFromPreview = async overrideQueueId => {
    let queueIdFinal = overrideQueueId;

    if (overrideQueueId === undefined) {
      const dec = resolveTakeoverQueue({
        ticket,
        user,
        pooledMode,
        status: "open"
      });
      if (dec.mode === "ask") {
        setSelectQueueModalOpen(true);
        return;
      }
      if (dec.mode === "auto") queueIdFinal = dec.queueId;
    }

    try {
      const { data } = await api.put(`/tickets/${ticketId}`, {
        status: "open",
        userId: user?.id,
        ...(queueIdFinal !== undefined ? { queueId: queueIdFinal } : {}),
      });
      setTicket(data);
      history.replace(`/tickets/${ticketId}`);
    } catch (err) {
      toastError(err);
    }
  };

  // FIX 2: compute marginRight based on which persistent drawers are open
  const mainMarginRight =
    -(contactDrawerWidth + appointmentDrawerWidth) +
    (drawerOpen ? contactDrawerWidth : 0) +
    (appointmentDrawerOpen ? appointmentDrawerWidth : 0);

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={classes.mainWrapper}
        style={{ marginRight: mainMarginRight }}
      >
        {/* Fix 5: banner de vista previa */}
        {readOnly && (
          <div style={{
            background: "#fff3cd",
            color: "#856404",
            padding: "6px 16px",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
            borderBottom: "1px solid #ffc107",
          }}>
            👁 Modo vista previa — los mensajes no se marcan como leídos
          </div>
        )}
        {incomingPendingAlert && (
          <div style={{
            background: "#fef3c7",
            color: "#92400e",
            padding: "8px 16px",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            flexShrink: 0,
            borderBottom: "1px solid #f59e0b",
          }}>
            <span>⏰ Este chat tiene un pendiente activo. ¿Qué hacés?</span>
            <div style={{ display: "flex", gap: 6 }}>
              <Button size="small" onClick={() => setIncomingPendingAlert(null)}>
                Mantener
              </Button>
              <Button
                size="small"
                style={{ color: "#ef4444" }}
                onClick={async () => {
                  try {
                    await resolvePending(incomingPendingAlert.id, "customer_replied");
                    setIncomingPendingAlert(null);
                    toast.success("Pendiente resuelto");
                  } catch (err) { toastError(err); }
                }}
              >
                Quitar pendiente
              </Button>
            </div>
          </div>
        )}
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              presence={presence}
              attendingMode={attendingMode}
              onClick={handleDrawerOpen}
            />
          </div>
          {!readOnly && (
            <div className={classes.ticketActionButtons} style={isCompact ? { gap: 2 } : undefined}>
              <ContactStateChip ticket={ticket} compact={isCompact} />
              <TicketPendingButton ticket={ticket} />
              <AppointmentButton
                ticket={ticket}
                onClick={() => setAppointmentDrawerOpen(o => !o)}
                open={appointmentDrawerOpen}
              />
              <QuickNotesPopover contact={contact} />
              <ModuleErrorBoundary module="Soporte/boton" variant="silent">
                <SupportButton ticket={ticket} />
              </ModuleErrorBoundary>
              {user?.profile === "admin" && (
                <Tooltip title={i18n.t("ticketActionButtons.exportTooltip")} arrow>
                  <IconButton onClick={handleExportTicket} size="small">
                    <GetAppIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {/* Separador visual entre iconos y botones de acción */}
              <span style={{ alignSelf: "center", color: "rgba(0,0,0,0.18)", fontSize: 20, userSelect: "none", lineHeight: 1 }}>|</span>
              <TicketActionButtons ticket={ticket} />
            </div>
          )}
        </TicketHeader>
        {!readOnly && ticket?.id && (
          <ChatConnectionBar ticket={ticket} onTicketUpdate={setTicket} />
        )}
        <ReplyMessageProvider key={ticketId}>
          <MessagesList
            ticketId={ticketId}
            isGroup={ticket.isGroup}
            readOnly={readOnly}
          />
          {!readOnly && (
            <SignatureOnboardingBanner
              user={user}
              signatureFeatureEnabled={signatureFeatureEnabled}
              onConfigClick={() => setUserProfileModalOpen(true)}
            />
          )}
          {/* Fix 5: ATENDER en readOnly, MessageInput normal en modo edición */}
          {readOnly ? (
            <div style={{ padding: 12, borderTop: "1px solid rgba(0,0,0,0.12)", flexShrink: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                onClick={() => handleTakeoverFromPreview()}
              >
                ATENDER
              </Button>
            </div>
          ) : (
            <MessageInput
              ticketStatus={ticket.status}
              contact={contact}
              ticket={ticket}
              attendingMode={attendingMode}
              onIntervention={() => setContact(prev => ({ ...prev, aiStatus: null, humanIntervention: true }))}
            />
          )}
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
      />
      <UserModal
        open={userProfileModalOpen}
        onClose={() => setUserProfileModalOpen(false)}
        userId={user?.id}
      />
      <AppointmentDrawer
        open={appointmentDrawerOpen}
        onClose={() => setAppointmentDrawerOpen(false)}
        ticket={ticket}
        contact={contact}
        rightOffset={drawerOpen ? contactDrawerWidth : 0}
      />
      {/* feat_345 — a qué cola va el cerrado ajeno que tomo desde la vista
          previa. No cierra solo: lo cierran onConfirm y onClose. */}
      <SelectQueueOnAcceptModal
        open={selectQueueModalOpen}
        onClose={() => setSelectQueueModalOpen(false)}
        onConfirm={queueId => {
          setSelectQueueModalOpen(false);
          handleTakeoverFromPreview(queueId);
        }}
        userQueues={user?.queues || []}
      />
    </div>
  );
};

export default Ticket;
